import { useCallback } from 'react';
import { ApiLoginWarning } from 'Constants/index';
import { redirect } from 'Utilities/browser';
import mediator from 'Utilities/mediator';
import { getAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { getLegacyToken } from 'Services/auth';
import useAuth from '../../useAuth';
const useCommercialApiAuth = () => {
  const {
    handleLoginError,
    handleLockoutError,
    handleForgotPasswordDone,
    handleResetPasswordDone,
    handleCountryRegionError,
    handleUnexpectedError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    navigateToPhoneVerification,
    authenticateUser,
    hasPendingConsents
  } = useAuth();
  const redirectUserAfterAuth = useCallback(async ({
    accessToken,
    accountId,
    app,
    regions,
    sharingWebviewsUrl,
    country,
    pId
  }) => {
    const legacyToken = await getLegacyToken(app, accountId, accessToken);
    const regionEntry = Object.entries(regions).find(([, {
      countries
    }]) => countries.includes(country));
    const region = regionEntry ? regionEntry[0] : 'eu';
    const legacyConnectedAppsUrl = `${sharingWebviewsUrl}?token=${legacyToken}&pId=${pId}&region=${region}`;
    redirect(legacyConnectedAppsUrl);
  }, []);
  const onConsentsUpdate = useCallback(() => {
    const authSession = getAuthSession();
    const {
      sharingWebviewsUrl,
      regions
    } = store.getState().config;
    if (authSession && sharingWebviewsUrl && regions) {
      const {
        app,
        pId
      } = store.getState().env;
      const {
        accessToken,
        accountId,
        country,
        authOrigin
      } = authSession;
      if (authOrigin === 'register') {
        mediator.publish('router:navigate', '/login');
        return;
      }
      redirectUserAfterAuth({
        app,
        accessToken,
        accountId,
        country,
        pId,
        regions,
        sharingWebviewsUrl
      });
      return;
    }
    mediator.publish('router:navigate', '/login');
  }, [redirectUserAfterAuth]);
  const authenticateUserAndRedirect = useCallback(async (email, password) => {
    const {
      app,
      pId
    } = store.getState().env;
    const {
      sharingWebviewsUrl,
      regions
    } = store.getState().config;
    if (!sharingWebviewsUrl || !regions) {
      return;
    }
    const {
      accessToken,
      accountId,
      verificationTasks,
      country,
      patient
    } = await authenticateUser(email, password);
    const unverifiedPhoneTask = verificationTasks?.find(task => task.verified === false && task.action === ApiLoginWarning.REGISTER_ACTION && task.verificationMethod === ApiLoginWarning.PHONE_VERIFICATION);
    if (!unverifiedPhoneTask) {
      if (hasPendingConsents(patient)) {
        return;
      }
      redirectUserAfterAuth({
        app,
        accessToken,
        accountId,
        country,
        pId,
        regions,
        sharingWebviewsUrl
      });
      return;
    }
    return navigateToPhoneVerification(unverifiedPhoneTask, async () => {
      const {
        accessToken,
        accountId,
        country,
        patient
      } = await authenticateUser(email, password);
      if (hasPendingConsents(patient)) {
        return;
      }
      redirectUserAfterAuth({
        app,
        accessToken,
        accountId,
        country,
        pId,
        regions,
        sharingWebviewsUrl
      });
    });
  }, [authenticateUser, hasPendingConsents, navigateToPhoneVerification, redirectUserAfterAuth]);
  return {
    authenticateUserAndRedirect,
    authenticateUser,
    onConsentsUpdate,
    handleLoginError,
    handleLockoutError,
    handleCountryRegionError,
    handleForgotPasswordDone,
    handleResetPasswordDone,
    handleUnexpectedError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage
  };
};
export default useCommercialApiAuth;